/**
 * CDP SDK Push Plugin
 *
 * Collects web-push subscribers without requiring the site owner to add
 * a service-worker file or a separate Push SDK script. The plugin renders
 * a dismissible banner on the host page; clicking "Allow" opens a top-level
 * popup window on the Push origin where the browser permission prompt can
 * actually fire (browsers block cross-origin iframe permission prompts).
 *
 * When the client can later host a service-worker file on the origin,
 * this plugin can be swapped for a direct Push SDK integration.
 */

import { definePlugin } from '../base';
import type { CDPPlugin, CDPCoreAPI } from '../base';

export interface PushPluginConfig {
  appId?: number;
  pushUrl?: string;
  delay?: number;
  title?: string;
  message?: string;
  acceptText?: string;
  denyText?: string;
  icon?: string;
  color?: string;
  autoDetect?: boolean;
  /** The site's language (Site Profile `language`): picks the defaults Push hands back. */
  language?: string;
  position?: BannerPosition;
  /** Path patterns ('/product/*'); empty = every page. `exclude` wins. */
  include?: string[];
  exclude?: string[];
  /** Days the banner keeps quiet after "No Thanks". */
  cooldownDays?: number;
}

export type BannerPosition = 'bottom-right' | 'bottom-left' | 'bottom' | 'top';

/**
 * The opt-in prompt as Push serves it (sdk-config `prompt`, edited in Push → Apps →
 * the app since 2026-09-28): the customer's values, null where they left a field
 * empty, plus the defaults in the site's language.
 */
interface RemotePrompt {
  title?: string | null;
  message?: string | null;
  accept?: string | null;
  deny?: string | null;
  color?: string | null;
  logo_url?: string | null;
  delay_seconds?: number | null;
  position?: BannerPosition | null;
  include?: string[];
  exclude?: string[];
  cooldown_days?: number | null;
  lang_used?: string;
  defaults?: {
    title?: string; message?: string; accept?: string; deny?: string;
    color?: string; delay_seconds?: number; position?: BannerPosition; cooldown_days?: number;
  };
}

interface HostPreset {
  appId: number;
  title: string;
  message: string;
  acceptText: string;
  denyText: string;
  icon: string;
  color: string;
}

const STORAGE_KEY = 'cdp_push_subscriber_id';
const DISMISS_KEY = 'cdp_push_dismissed_at';
const DAY_MS = 24 * 60 * 60 * 1000;
const DISMISS_COOLDOWN_MS = 7 * DAY_MS; // 7 days — the default; the editor can change it
// "Block" in the browser's prompt stays until the visitor changes it in the browser's
// settings. Until 2026-09-28 the plugin only logged it, and the banner came back on the
// next page, every page. Re-offered after 90 days: the popup reports the block again
// on open if it still stands.
const BLOCKED_KEY = 'cdp_push_blocked_at';
const BLOCKED_COOLDOWN_MS = 90 * 24 * 60 * 60 * 1000;

// ---------------------------------------------------------------------------
// This browser's subscriber, for the CDP profile (2026-09-29, decision Р6)
// ---------------------------------------------------------------------------
// The point: a cart reminder by push for a visitor who never gave an e-mail. The
// popup answers a subscription with {subscriber id, link token}; the plugin keeps
// them here and reports them, and the CDP asks Push before it writes the
// subscriber onto this visitor's profile. The token proves the claim and does
// nothing else (it cannot unsubscribe anyone).
const LINK_KEY = 'cdp_push_link';
const LINK_SEEN_KEY = 'cdp_push_link_seen';
const LINK_PARAMS = ['mpa', 'mpid', 'mpt'];

export interface PushLink { app: number; sid: number; token: string }

function toLink(app: unknown, sid: unknown, token: unknown): PushLink | null {
  const a = Number(app);
  const s = Number(sid);
  if (!Number.isInteger(a) || a <= 0 || !Number.isInteger(s) || s <= 0) return null;
  if (typeof token !== 'string' || !/^[0-9a-f]{64}$/.test(token)) return null;
  return { app: a, sid: s, token };
}

function readLink(): PushLink | null {
  try {
    const raw = JSON.parse(localStorage.getItem(LINK_KEY) || 'null');
    return raw ? toLink(raw.app, raw.sid, raw.token) : null;
  } catch { return null; }
}

function storeLink(link: PushLink): void {
  try { localStorage.setItem(LINK_KEY, JSON.stringify(link)); } catch {}
}

/** Set by capturePushClickLink(); reported once by reportPushLink(). */
let clickLink: PushLink | null = null;

/**
 * A notification click opens the store with mpa/mpid/mpt (the worker on
 * push.monfri.net adds them). Take them out of the address BEFORE the first page
 * view: no page event, referrer or other tag on the page may carry the token.
 * The full bundle calls this ahead of init.
 */
export function capturePushClickLink(): void {
  if (typeof window === 'undefined') return;
  try {
    const search = window.location.search;
    if (!/[?&](mpa|mpid|mpt)=/.test(search)) return;
    const params = new URLSearchParams(search);
    const link = toLink(params.get('mpa'), params.get('mpid'), params.get('mpt'));
    // Only the three pairs go; the rest of the query stays byte for byte.
    const kept = search.slice(1).split('&').filter((pair) => {
      let name = pair.split('=')[0];
      try { name = decodeURIComponent(name); } catch { /* keep the raw name */ }
      return pair !== '' && LINK_PARAMS.indexOf(name) === -1;
    });
    const url = window.location.pathname + (kept.length ? '?' + kept.join('&') : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', url);
    if (link) clickLink = link;
  } catch { /* leave the address as it is */ }
}

interface LinkReporter {
  track: (event: string, properties?: Record<string, unknown>) => void;
  hasConsent?: (type: 'marketing') => boolean;
}

/**
 * After init: after a notification click, and otherwise once a day, tell the CDP
 * which subscriber this browser is — the profile behind it may have changed (a
 * login, a merge, cleared cookies). Tying a browser to a profile for marketing
 * waits for marketing consent, as the banner does (Р5); on an implied site that
 * is granted from the start.
 */
export function reportPushLink(api: LinkReporter): void {
  if (typeof window === 'undefined') return;
  const consented = (): boolean => {
    try { return !api.hasConsent || api.hasConsent('marketing'); } catch { return true; }
  };
  const send = (): void => {
    const via = clickLink ? 'click' : 'visit';
    const link = clickLink || readLink();
    clickLink = null;
    if (!link) return;
    const today = new Date().toISOString().slice(0, 10);
    try {
      if (via === 'visit' && localStorage.getItem(LINK_SEEN_KEY) === today) return;
      if (via === 'click') storeLink(link);
      localStorage.setItem(LINK_SEEN_KEY, today);
    } catch { if (via === 'visit') return; }
    try {
      api.track('Push Subscriber Seen', {
        push_app_id: link.app, push_subscriber_id: link.sid, push_link_token: link.token, via,
      });
    } catch {}
  };
  if (!clickLink && !readLink()) return;
  if (consented()) { send(); return; }
  const onConsent = (e: Event): void => {
    const detail = (e as CustomEvent).detail || {};
    if (detail.marketing !== true) return;
    window.removeEventListener('cdp:consent', onConsent);
    send();
  };
  window.addEventListener('cdp:consent', onConsent);
}

/**
 * DEPRECATED — per-customer config baked into a shared bundle. Onboarding a
 * customer must never need an SDK release; the Site Profile's `push` block
 * (served by /v1/config, passed in as userConfig by the full bundle) is the
 * supported path and takes precedence over everything here.
 *
 * Kept ONLY so optibox.bg keeps its Bulgarian copy + logo until its Site
 * Profile carries them — removing this map before that lands would silently
 * downgrade its banner to the generic English FALLBACKS. Delete this map (and
 * getHostPreset/isPushEnabledHost's dependence on it) once the profile is
 * populated.
 */
const HOST_PRESETS: Record<string, HostPreset> = {
  'optibox.bg': {
    appId: 3,
    title: 'Новини от OptiBox',
    message: 'Получавайте първи нашите оферти, намаления и новости.',
    acceptText: 'Разрешавам',
    denyText: 'Не сега',
    icon: 'https://cdncloudcart.com/31223/logo/1.svg',
    color: '#1E3A8A',
  },
  'www.optibox.bg': {
    appId: 3,
    title: 'Новини от OptiBox',
    message: 'Получавайте първи нашите оферти, намаления и новости.',
    acceptText: 'Разрешавам',
    denyText: 'Не сега',
    icon: 'https://cdncloudcart.com/31223/logo/1.svg',
    color: '#1E3A8A',
  },
};

function getHostPreset(): HostPreset | null {
  if (typeof window === 'undefined') return null;
  return HOST_PRESETS[window.location.hostname] || null;
}

const FALLBACKS = {
  pushUrl: 'https://push.monfri.net',
  delay: 3000,
  title: 'Stay in the loop',
  message: 'Get notified about offers, updates, and news.',
  acceptText: 'Allow',
  denyText: 'No Thanks',
  icon: '',
  color: '#4F46E5',
  autoDetect: true,
};

export function isPushEnabledHost(): boolean {
  return getHostPreset() !== null;
}

function buildPopupUrl(cfg: Required<PushPluginConfig>): string {
  const params = new URLSearchParams({
    origin: window.location.origin,
    title: cfg.title,
    message: cfg.message,
    acceptText: cfg.acceptText,
    denyText: cfg.denyText,
    color: cfg.color,
  });
  if (cfg.icon) params.set('icon', cfg.icon);
  if (cfg.language) params.set('lang', cfg.language);
  return `${cfg.pushUrl.replace(/\/+$/, '')}/subscribe-popup/${cfg.appId}?${params.toString()}`;
}

function injectBannerStyles(color: string): void {
  if (document.getElementById('cdp-push-banner-styles')) return;
  const style = document.createElement('style');
  style.id = 'cdp-push-banner-styles';
  style.textContent = `
    #cdp-push-banner {
      position: fixed;
      bottom: 16px;
      right: 16px;
      z-index: 2147483000;
      max-width: 360px;
      width: calc(100vw - 32px);
      background: #ffffff;
      border-radius: 14px;
      border: 1px solid rgba(15, 23, 42, 0.08);
      box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.18), 0 6px 14px rgba(0, 0, 0, 0.06);
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif;
      color: #0f172a;
      animation: cdpPushIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      overflow: hidden;
    }
    @keyframes cdpPushIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes cdpPushOut { to { transform: translateY(30px); opacity: 0; } }
    #cdp-push-banner.cdp-out { animation: cdpPushOut 0.25s ease forwards; }
    #cdp-push-banner .cdp-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px 10px;
    }
    #cdp-push-banner img {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      padding: 5px;
      background: #fafafa;
      border: 1px solid rgba(15, 23, 42, 0.06);
      object-fit: contain;
      flex-shrink: 0;
    }
    #cdp-push-banner .cdp-title {
      font-size: 15px;
      font-weight: 700;
      letter-spacing: -0.01em;
      line-height: 1.3;
    }
    #cdp-push-banner .cdp-msg {
      padding: 0 16px 12px;
      font-size: 13px;
      color: #64748b;
      line-height: 1.5;
    }
    #cdp-push-banner .cdp-actions {
      display: flex;
      gap: 8px;
      padding: 10px 12px;
      background: #fafafa;
      border-top: 1px solid rgba(15, 23, 42, 0.06);
      justify-content: flex-end;
    }
    #cdp-push-banner button {
      padding: 8px 16px;
      font-size: 13.5px;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.15s ease;
      font-family: inherit;
      letter-spacing: 0.01em;
      border: 1px solid transparent;
    }
    #cdp-push-banner .cdp-deny {
      background: transparent;
      color: #64748b;
      border-color: rgba(15, 23, 42, 0.12);
    }
    #cdp-push-banner .cdp-deny:hover { background: #f1f5f9; color: #334155; }
    #cdp-push-banner .cdp-accept {
      background: ${color};
      color: #ffffff;
      border: none;
      box-shadow: 0 1px 3px ${color}33;
    }
    #cdp-push-banner .cdp-accept:hover { transform: translateY(-1px); box-shadow: 0 4px 12px ${color}4d; }
    #cdp-push-banner .cdp-accept:active { transform: translateY(0); }
  `;
  document.head.appendChild(style);
}

function createBanner(cfg: Required<PushPluginConfig>, onAccept: () => void, onDeny: () => void): HTMLDivElement {
  injectBannerStyles(cfg.color);
  const el = document.createElement('div');
  el.id = 'cdp-push-banner';
  // Bottom right is the stylesheet's own; the others centre with auto margins, which
  // leaves `transform` to the slide-in animation.
  if (cfg.position === 'bottom-left') { el.style.right = 'auto'; el.style.left = '16px'; }
  else if (cfg.position === 'bottom') { el.style.left = '0'; el.style.right = '0'; el.style.margin = '0 auto'; }
  else if (cfg.position === 'top') { el.style.bottom = 'auto'; el.style.top = '16px'; el.style.left = '0'; el.style.right = '0'; el.style.margin = '0 auto'; }
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-label', cfg.title);

  const row = document.createElement('div');
  row.className = 'cdp-row';
  if (cfg.icon) {
    const img = document.createElement('img');
    img.src = cfg.icon;
    img.alt = '';
    row.appendChild(img);
  }
  const titleEl = document.createElement('div');
  titleEl.className = 'cdp-title';
  titleEl.textContent = cfg.title;
  row.appendChild(titleEl);
  el.appendChild(row);

  const msgEl = document.createElement('div');
  msgEl.className = 'cdp-msg';
  msgEl.textContent = cfg.message;
  el.appendChild(msgEl);

  const actions = document.createElement('div');
  actions.className = 'cdp-actions';
  const denyBtn = document.createElement('button');
  denyBtn.className = 'cdp-deny';
  denyBtn.textContent = cfg.denyText;
  denyBtn.addEventListener('click', onDeny);
  const acceptBtn = document.createElement('button');
  acceptBtn.className = 'cdp-accept';
  acceptBtn.textContent = cfg.acceptText;
  acceptBtn.addEventListener('click', onAccept);
  actions.appendChild(denyBtn);
  actions.appendChild(acceptBtn);
  el.appendChild(actions);

  return el;
}

export function createPushPlugin(userConfig: PushPluginConfig = {}): CDPPlugin {
  const preset = getHostPreset();
  // What the site profile, an explicit configurePush() or the legacy preset say. The
  // customer's own values from Push (the editor) win over these; Push's defaults in
  // the site's language come after them (decisions Р1–Р3, 2026-09-28).
  const explicit = {
    title: userConfig.title ?? preset?.title,
    message: userConfig.message ?? preset?.message,
    acceptText: userConfig.acceptText ?? preset?.acceptText,
    denyText: userConfig.denyText ?? preset?.denyText,
    icon: userConfig.icon ?? preset?.icon,
    color: userConfig.color ?? preset?.color,
  };
  const config: Required<PushPluginConfig> = {
    appId: userConfig.appId ?? preset?.appId ?? 0,
    pushUrl: userConfig.pushUrl ?? FALLBACKS.pushUrl,
    delay: userConfig.delay ?? FALLBACKS.delay,
    title: explicit.title ?? FALLBACKS.title,
    message: explicit.message ?? FALLBACKS.message,
    acceptText: explicit.acceptText ?? FALLBACKS.acceptText,
    denyText: explicit.denyText ?? FALLBACKS.denyText,
    icon: explicit.icon ?? FALLBACKS.icon,
    color: explicit.color ?? FALLBACKS.color,
    autoDetect: userConfig.autoDetect ?? FALLBACKS.autoDetect,
    language: userConfig.language ?? '',
    position: userConfig.position ?? 'bottom-right',
    include: userConfig.include ?? [],
    exclude: userConfig.exclude ?? [],
    cooldownDays: userConfig.cooldownDays ?? 7,
  };
  let unmounted = false;
  let consentListener: ((e: Event) => void) | null = null;

  /**
   * Р5 (2026-09-28): the banner follows the site's consent mode. On an implied site
   * marketing is granted from the start (unless GPC/DNT or an explicit refusal), so
   * nothing changes there; on a strict / cmp-only site the banner waits until the
   * CMP grants marketing. The browser's own prompt stays the consent to push itself.
   */
  function consentOk(): boolean {
    try { return !core || core.hasConsent('marketing'); } catch { return true; }
  }

  function waitForConsent(): void {
    if (consentListener || typeof window === 'undefined') return;
    consentListener = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.marketing !== true) return;
      if (consentListener) window.removeEventListener('cdp:consent', consentListener);
      consentListener = null;
      mount();
    };
    window.addEventListener('cdp:consent', consentListener);
  }

  function siteLanguage(): string {
    if (config.language) return config.language;
    try { return document.documentElement.lang || ''; } catch { return ''; }
  }

  function applyRemote(p: RemotePrompt): void {
    const d = p.defaults || {};
    config.title = p.title ?? explicit.title ?? d.title ?? config.title;
    config.message = p.message ?? explicit.message ?? d.message ?? config.message;
    config.acceptText = p.accept ?? explicit.acceptText ?? d.accept ?? config.acceptText;
    config.denyText = p.deny ?? explicit.denyText ?? d.deny ?? config.denyText;
    config.color = p.color ?? explicit.color ?? d.color ?? config.color;
    config.icon = p.logo_url ?? explicit.icon ?? config.icon;
    if (p.delay_seconds != null) config.delay = p.delay_seconds * 1000;
    else if (userConfig.delay == null && d.delay_seconds != null) config.delay = d.delay_seconds * 1000;
    config.position = p.position ?? userConfig.position ?? d.position ?? config.position;
    if (p.include && p.include.length) config.include = p.include;
    if (p.exclude && p.exclude.length) config.exclude = p.exclude;
    config.cooldownDays = p.cooldown_days ?? userConfig.cooldownDays ?? d.cooldown_days ?? config.cooldownDays;
    if (p.lang_used) config.language = p.lang_used;
  }

  /** The prompt from Push. Any failure leaves the local config as it is (fail open). */
  async function loadRemote(): Promise<void> {
    if (typeof fetch !== 'function' || !config.appId) return;
    const url = `${config.pushUrl.replace(/\/+$/, '')}/api/v1/apps/${config.appId}/sdk-config?lang=${encodeURIComponent(siteLanguage())}`;
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 4000) : null;
    try {
      // A plain GET, no custom headers: no CORS preflight. (Push answers with a
      // 5-minute browser cache, so a visitor asks once per five minutes at most.)
      const res = await fetch(url, { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined });
      if (!res.ok) return;
      const body = await res.json();
      const prompt = body && body.data && body.data.prompt;
      if (prompt && typeof prompt === 'object') applyRemote(prompt as RemotePrompt);
    } catch { /* keep what we have */ } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /** Include / exclude path patterns; '*' matches anything, the rest is literal. */
  function onThisPage(): boolean {
    let path = '/';
    try { path = window.location.pathname || '/'; } catch { /* keep '/' */ }
    const matches = (pattern: string): boolean => {
      const source = pattern.split('*').map((part) => part.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*');
      try { return new RegExp('^' + source + '$').test(path); } catch { return false; }
    };
    if (config.exclude.some(matches)) return false;
    return config.include.length === 0 || config.include.some(matches);
  }

  let banner: HTMLDivElement | null = null;
  let messageHandler: ((e: MessageEvent) => void) | null = null;
  let popup: Window | null = null;
  let showTimer: ReturnType<typeof setTimeout> | null = null;
  let core: CDPCoreAPI | null = null;

  function expectedOrigin(): string {
    try { return new URL(config.pushUrl).origin; } catch { return ''; }
  }

  function removeBanner(animate = true): void {
    if (!banner) return;
    const el = banner;
    banner = null;
    if (!animate) {
      if (el.parentNode) el.parentNode.removeChild(el);
      return;
    }
    el.classList.add('cdp-out');
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 260);
  }

  function markDismissed(): void {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  }

  function markBlocked(): void {
    try { localStorage.setItem(BLOCKED_KEY, String(Date.now())); } catch {}
  }

  function markSubscribed(id: number): void {
    try { localStorage.setItem(STORAGE_KEY, String(id)); } catch {}
  }

  function openPopup(): void {
    const url = buildPopupUrl(config);
    const w = 440;
    const h = 440;
    const left = Math.max(0, Math.floor((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.floor((window.screen.availHeight - h) / 2));
    const features = `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes`;
    popup = window.open(url, 'cdp-push-subscribe', features);
    if (!popup) {
      if (core) core.log('[cdp-push] popup blocked');
      return;
    }
  }

  function handleMessage(event: MessageEvent): void {
    if (event.origin !== expectedOrigin()) return;
    const data = event.data || {};
    if (data.source !== 'cdp-push') return;

    switch (data.type) {
      case 'subscribed': {
        const id = Number(data.subscriberId);
        if (!Number.isFinite(id)) return;
        markSubscribed(id);
        removeBanner();
        // The link token lets the CDP tie this subscriber to the visitor's profile
        // (Р6). The identify that used to follow is gone: it wrote an unchecked
        // push_subscriber_id trait nobody read and re-fired profile.identified.
        const link = toLink(config.appId, id, data.linkToken);
        if (link) {
          storeLink(link);
          try { localStorage.setItem(LINK_SEEN_KEY, new Date().toISOString().slice(0, 10)); } catch {}
        }
        if (core) {
          try {
            core.track('Push Subscribed', {
              push_subscriber_id: id,
              push_app_id: config.appId,
              ...(link ? { push_link_token: link.token } : {}),
            });
          } catch {}
        }
        break;
      }
      case 'dismissed': {
        markDismissed();
        removeBanner();
        break;
      }
      case 'blocked': {
        // The popup opened on a browser that already blocks notifications for it.
        markBlocked();
        removeBanner();
        break;
      }
      case 'subscribe-null':
      case 'subscribe-error': {
        if (core) core.log('[cdp-push] popup subscribe failed', data.type, data.permission || '', data.message || '');
        if (data.permission === 'denied') {
          markBlocked();
          removeBanner();
        }
        break;
      }
      case 'closed':
      case 'subscribe-attempt':
      case 'error':
        break;
    }
  }

  function shouldShow(): boolean {
    if (!config.appId) return false;
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    // Notifications blocked for this site itself: nothing a banner here can change.
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return false;
    try {
      if (localStorage.getItem(STORAGE_KEY)) return false;
      const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || '0');
      const cooldownMs = config.cooldownDays > 0 ? config.cooldownDays * DAY_MS : DISMISS_COOLDOWN_MS;
      if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return false;
      const blockedAt = Number(localStorage.getItem(BLOCKED_KEY) || '0');
      if (blockedAt && Date.now() - blockedAt < BLOCKED_COOLDOWN_MS) return false;
    } catch {}
    return true;
  }

  function showBanner(): void {
    if (banner) return;
    if (!shouldShow()) return;

    messageHandler = handleMessage;
    window.addEventListener('message', messageHandler);

    banner = createBanner(
      config,
      () => openPopup(),
      () => { markDismissed(); removeBanner(); },
    );
    if (document.body) document.body.appendChild(banner);
  }

  function mount(): void {
    if (unmounted || !shouldShow()) return;
    if (!consentOk()) { waitForConsent(); return; }
    void loadRemote().then(() => {
      // The customer's pages or cooldown can rule this visit out after all.
      if (unmounted || !shouldShow() || !onThisPage()) return;
      const attach = () => {
        if (unmounted) return;
        if (!document.body) { setTimeout(attach, 100); return; }
        if (config.delay > 0) {
          showTimer = setTimeout(showBanner, config.delay);
        } else {
          showBanner();
        }
      };
      attach();
    });
  }

  function unmount(): void {
    unmounted = true;
    if (consentListener) window.removeEventListener('cdp:consent', consentListener);
    consentListener = null;
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    removeBanner(false);
    if (messageHandler) window.removeEventListener('message', messageHandler);
    messageHandler = null;
    if (popup && !popup.closed) { try { popup.close(); } catch {} }
    popup = null;
  }

  return definePlugin({
    name: 'push-subscriber',
    version: '2.0.0',
    install(coreApi) { core = coreApi; },
    onInit() { mount(); },
    // A hidden tab is not a closed page: without this, every tab switch
    // unmounted the opt-in banner for the rest of the page's life.
    onHidden() { /* keep the banner */ },
    onUnload() { unmount(); },
    config,
  });
}

export default createPushPlugin;
